import React, { forwardRef, useContext, useRef } from 'react';
import { bool, func, string, shape, node } from 'prop-types';
import { ButtonStyled, TextStyled } from '@nike/nike-design-system-components';
import { useKeyboard } from 'react-aria';
import useLocale from '../../utils/hooks/localeHook';
import { AnalyticsContext } from '../../context/analyticsContext';
import { triggers } from '../../utils/triggers';

/**
 * An instrumented anchor or button.
 * This component can either be passed text to be set directly as props,
 * or used as a wrapper for other content.
 * @param {Object} props
 * @param {Object} providedRef
 * @param {HTMLElement} props.children
 * @param {String} props.ariaLabel aria-label for accessibility.
 * @param {String} props.className class provided by parent for targeted styling.
 * @param {String} props.href where the Link is going
 * @param {String} props.text name/title/other text that is being set.
 * @param {Object} props.clickEventPayload Name and data that is being sent for analytics.
 * @param {Boolean} props.shouldSendEvent Flag to determine if the event should be sent.
 * @param {String} props.dataTestId data-testid for testing.
 * @param {Function} props.onClick function to run on click.
 * @param {Function} props.onPopup function to run on popup. This is needed for accessibility
 * when user presses ctl-option-space. This is used for dropdowns when a user is using Voice Over
 * any parameters not enumerated here will be passed into the anchor or button tag on the dom
 * @returns {HTMLElement} ButtonStyled or a
 */

// eslint-disable-next-line react/display-name
const Link = forwardRef((props, providedRef) => {
  const {
    Icon,
    ariaLabel,
    children: componentChildren,
    className,
    clickEventPayload,
    shouldSendEvent = true,
    href,
    text,
    dataTestId,
    onClick,
    onPopup,
    ...rest
  } = props;

  const { analyticsClient } = useContext(AnalyticsContext);
  const locale = useLocale();

  const localRef = useRef(null);
  const ref = providedRef || localRef;
  const handleClick = event => {
    /* 
      Adding event.preventDefault prevents the native default action triggered 
      when a link is clicked. This will ensure all code in the method will be executed.
    */
    event.preventDefault();

    if (onClick) {
      onClick(event);
    }
    // Send the analytics event with data.
    if (shouldSendEvent && clickEventPayload?.type) {
      analyticsClient?.lookup(clickEventPayload);
    }

    /*
      If href is present, check the triggers to override the native behavior, else treat href normally, 
    */
    if (href) {
      const triggerArgument = {
        locale,
      };
      if (triggers[href]) return triggers[href](triggerArgument);
      window.location.assign(href);
    }
  };
  const { keyboardProps } = useKeyboard({
    onKeyDown: event => {
      switch (event.key) {
        case ' ': // SPACE
          if (event.target.getAttribute('aria-haspopup') === 'menu' && onPopup) {
            event.preventDefault();
            onPopup(event);
          } else {
            event.nativeEvent.preventDefault(); // Prevent scroll when using space bar.
            ref.current.click();
          }
          break;
        default:
          // ensures an Escape key press event, for instance, will bubble up to a higher keyboard handler
          event.continuePropagation();
          break;
      }
    },
  });

  const testId = dataTestId || 'link';
  const ButtonOrLink = href ? 'a' : ButtonStyled;
  const buttonOrLinkProps = href
    ? { href }
    : { appearance: 'text', disableRipple: true, Icon, iconPlacement: 'start' };

  return (
    <ButtonOrLink
      {...rest}
      aria-label={ariaLabel}
      className={className || ''}
      data-testid={testId}
      onClick={handleClick}
      {...buttonOrLinkProps}
      {...keyboardProps}
      ref={ref}
    >
      {componentChildren && componentChildren}
      {text && (
        <TextStyled appearance="body3Strong" className={className} color="secondary">
          {text}
        </TextStyled>
      )}
    </ButtonOrLink>
  );
});

export default Link;

Link.propTypes = {
  ariaLabel: string,
  children: node,
  className: string,
  clickEventPayload: shape({}),
  shouldSendEvent: bool,
  href: string,
  Icon: node,
  onClick: func,
  text: string,
  dataTestId: string,
  onPopup: func,
};
